import { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteFallback } from './RouteFallback';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import TopNavbar from './TopNavbar';
import ScrollToTop from './ScrollToTop';
import { FeedbackButton } from '@/components/feedback/FeedbackButton';
import { WhatsNewLaunchDialog } from '@/components/whats-new/WhatsNewLaunchDialog';
import { idpEnabled } from '@/lib/idp';
import { useCompany } from '@/contexts/CompanyContext';
import { usePermissions } from '@/hooks/usePermissions';
import { getSupplierNavigationSections } from './supplierNavigation';
import { supplierInitials } from './supplierInitials';

function SupplierOrgChip({ name, isAdmin }: { name: string; isAdmin: boolean }) {
  return (
    <div className="mx-4 mb-1 flex items-center gap-2.5 rounded-xl bg-surface px-3 py-2.5 dark:bg-dark-hover">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-[12px] font-extrabold text-white">
        {supplierInitials(name)}
      </div>
      <div className="min-w-0">
        <p className="truncate text-[13px] font-bold leading-[18px] text-dark dark:text-dark-text">{name}</p>
        <p className="text-[11px] leading-4 text-muted dark:text-dark-muted">
          Leverandør · {isAdmin ? 'Administrator' : 'Sælger'}
        </p>
      </div>
    </div>
  );
}

/**
 * The shell for a Supplier-plan organisation: the account manager's own menu, not the
 * company menu. Same chrome as AdminLayout (sidebar, top bar, mobile drawer); the
 * pages come from the /supplier routes.
 */
export default function SupplierLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { activeCompanyName } = useCompany();
  const { isAdmin } = usePermissions();
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobileMenuOpen(false); };
    const desktop = window.matchMedia('(min-width: 1024px)');
    const resize = () => { if (desktop.matches) setMobileMenuOpen(false); };
    window.addEventListener('keydown', escape);
    desktop.addEventListener('change', resize);
    return () => { window.removeEventListener('keydown', escape); desktop.removeEventListener('change', resize); };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-surface dark:bg-dark-surface">
      <ScrollToTop />
      {mobileMenuOpen && <button aria-label="Luk menu" className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />}
      <div className={mobileMenuOpen ? 'block' : 'hidden lg:block'}>
        <Sidebar
          sections={getSupplierNavigationSections({ isAdmin })}
          header={<SupplierOrgChip name={activeCompanyName ?? ''} isAdmin={isAdmin} />}
        />
      </div>
      <div className="ml-0 lg:ml-[270px] min-h-screen min-w-0 flex flex-col" inert={mobileMenuOpen ? true : undefined}>
        <TopNavbar onOpenMobileMenu={() => setMobileMenuOpen(true)} />
        <main className="flex-1 p-4 sm:p-6">
          <ErrorBoundary><Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense></ErrorBoundary>
        </main>
      </div>
      <FeedbackButton />
      {idpEnabled && <WhatsNewLaunchDialog />}
    </div>
  );
}
